import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Paper, Alert } from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { useQuery } from '@tanstack/react-query';
import { getBusinessConfigById } from '../../firebase/firebase-delegate';
import { BusinessConfig } from '../../types';
import { resolveBookingUxConfig } from '../../utils/bookingUxConfig';

const ManageSitePage: React.FC = () => {
    const { siteId } = useParams();
    const navigate = useNavigate();
    const [isNewSite, setIsNewSite] = useState(false);
    const [checking, setChecking] = useState(true);

    const businessConfigQuery = useQuery<BusinessConfig | null>({
        queryKey: ['businessConfig', siteId],
        enabled: !!siteId,
        queryFn: () => getBusinessConfigById(siteId!)
    });
    const bookingUxConfig = resolveBookingUxConfig(businessConfigQuery.data);
    const resourceSingular = bookingUxConfig.terminology.resourceSingular;

    useEffect(() => {
        const checkIfSiteExists = async () => {
            if (!siteId) return;

            const configDocRef = doc(db, 'booking-websites', siteId, 'config', 'businessInfo');
            const docSnap = await getDoc(configDocRef);

            setIsNewSite(!docSnap.exists());
            setChecking(false);
        };

        checkIfSiteExists();
    }, [siteId]);

    const handleEditConfigClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/businessConfig`);
    };

    const handleAddEmployeeClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/add-employee`);
    };

    const handleManageServicesClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/manage-services`);
    };

    const handleManageHomepageReviewsClick = () => {
        navigate(`/admin/superadmin/manage/${siteId}/homepage-reviews`);
    };

    return (
        <Box p={4}>
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Manage Site: <strong>{businessConfigQuery.data?.businessInfo.name}</strong>
                </Typography>

                {!checking && isNewSite && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        This site does not currently exist in the database. A new record will be created when you save a config.
                    </Alert>
                )}

                <Typography variant="body1" gutterBottom>
                    Use the options below to manage this tenant.
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
                    <Button variant="contained" onClick={handleEditConfigClick}>
                        Edit/Add Business Config
                    </Button>
                    <Button variant="contained" onClick={handleManageServicesClick}>
                        Edit/Add a Service
                    </Button>
                    <Button variant="contained" onClick={handleManageHomepageReviewsClick}>
                        Manage Homepage Reviews
                    </Button>
                    <Button variant="contained" onClick={handleAddEmployeeClick}>
                        {`Add ${resourceSingular}`}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default ManageSitePage;
